import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import AntGridScope, { buildGridScopeSegment } from '../components/AntGridScope';
import { useLocation, useNavigate } from 'react-router-dom';
import stringify from '../utils/stableStringify';
import { useReactToPrint } from 'react-to-print';
import Box, { Item as BoxItem } from 'devextreme-react/box';

import { LoadPanel } from 'devextreme-react/load-panel';
import AntCondition from '../utils/AntCondition';
import AntConfig from '../utils/AntConfig';
import AntLocalization from '../utils/AntLocalization';
import AntPage from './AntPOSPage';
import AntToolbar from './AntPOSToolbar';
import AntPOSVoucherActions from './AntPOSVoucherActions';
import AntBaseForm from './AntPOSForm';
import AntVoucherTabs from './AntPOSVoucherTabs';
import AntErrorPage from './AntPOSErrorPage';
import AntVoucherSkeleton from '../components/AntVoucherSkeleton';
import AntErrorPanel from './AntPOSErrorPanel';
import AntInfoPanel from './AntPOSInfoPanel';
import AntMessageBox from './AntPOSMessageBox';

import AntPrinting from './AntPOSPrinting';
import AntObjectEditPopup from './AntPOSObjectEditPopup';

import AntAuth from '../utils/AntAuth';
import { applyPreparedSnapshot } from '../utils/AntSnapshotTransport';
import cs from '../utils/const';

import { AntGlobal, AntSession } from '../utils/AntContext';

const FETCH_ERROR_MSG = {
    vi: 'Không thể kết nối đến hệ thống !',
    en: 'Can not connect to system !',
    fr: 'Impossible de se connecter au système !',
    es: '¡No se puede conectar al sistema!',
    id: 'Tidak dapat terhubung ke sistem!',
    ja: 'システムに接続できません！',
    zh: '无法连接到系统！',
    ko: '시스템에 연결할 수 없습니다!',
    hi: 'सिस्टम से कनेक्ट नहीं हो सकता!',
    ar: 'لا يمكن الاتصال بالنظام!'
};

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    const locationPath = String(location.pathname);

    if (props.API && !props.API.entityDisplay && appGlobal?.appConfig?.Services) {
        const found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
        if (found) props.API.init({ entityDisplay: found.display });
    }

    const [shouldRender, setShouldRender] = useState(1);
    const forceRender = useCallback(() => setShouldRender((v) => v + 1), []);

    const mainTable = props.table || props.API?.entityName || '';
    const mainEntityDisplay = props.API?.entityDisplay || props.entityDisplay || '';

    const mainConfig = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === mainTable);
        if (found) return found.config;

        const cnf = new AntConfig(appGlobal, mainTable);
        if (!Array.isArray(appGlobal.tableConfigs)) appGlobal.tableConfigs = [];
        appGlobal.tableConfigs.push({ tableName: mainTable, config: cnf });
        return cnf;
    }, [mainTable, appGlobal]);

    const [mainData, setMainData] = useState({});
    const [footerData, setFooterData] = useState({});
    const mainCode = useRef(null);
    const mainID = useRef(null);
    const [mainState, setMainState] = useState({
        error: null,
        tabStyle: 'Collapse',
        oldData: null,
        loading: false,
        toolbarProcessingButton: null
    });

    const mainInfos = useMemo(() => {
        if (!Array.isArray(footerData?.infos) || footerData.infos.length === 0) return [];
        return props.onProcessInfo ? props.onProcessInfo(footerData.infos) : footerData.infos;
    }, [footerData, props.onProcessInfo]);

    const mainDataRef = useRef(mainData);
    mainDataRef.current = mainData;
    const loadSeqRef = useRef(0);
    const summarySeqRef = useRef(0);
    const dataVersionRef = useRef(null);

    // Wrapper: set mainData + footerData dong thoi (full reload, navigate, action...)
    const setAllData = useCallback((updater) => {
        setMainData((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (next === prev) return prev;
            setFooterData(next);
            return next;
        });
    }, []);

    const applyPreparedData = useCallback(
        (payload) => {
            const obj = applyPreparedSnapshot(mainDataRef.current, payload);
            if (!obj) return false;
            if (mainCode.current && obj.code && obj.code !== mainCode.current) return false;
            if (mainID.current && obj.id && String(obj.id) !== String(mainID.current)) return false;
            if (obj.updated_at && dataVersionRef.current && obj.updated_at < dataVersionRef.current) return false;

            if (quickReloadTimeOut.current) {
                clearTimeout(quickReloadTimeOut.current);
                quickReloadTimeOut.current = null;
            }

            loadSeqRef.current += 1;
            summarySeqRef.current += 1;
            if (obj.updated_at) dataVersionRef.current = obj.updated_at;
            obj.edit = obj.edit || {};
            obj.edit.always_editable_fields = [...new Set([...(obj.edit.always_editable_fields || []), 'code'])];
            obj.edit.none_validate_fields = [...new Set([...(obj.edit.none_validate_fields || []), 'code'])];

            mainDataRef.current = obj;
            mainCode.current = obj.code;
            mainID.current = obj.id;
            isPrepared.current = true;
            setAllData((current) => (stringify(current) === stringify(obj) ? current : { ...obj }));
            if (obj.items instanceof Array) tabs.current?.replaceGridRows?.('items', obj.items);
            setMainState((state) => {
                if (!state.error && !state.loading && stringify(state.oldData) === stringify(obj)) return state;
                return { ...state, error: null, loading: false, oldData: JSON.parse(JSON.stringify(obj)) };
            });
            return true;
        },
        [setAllData]
    );

    const isPrepared = useRef(false);
    // Khung xám CHỈ khi mở sang MỘT phiếu khác (mã đổi). Tải lại phiếu đang mở —
    // sau thao tác, tín hiệu thời gian thực, hẹn giờ — giữ nguyên nội dung đang hiện.
    const [bootCode, setBootCode] = useState(null);
    const isSaving = useRef(false);
    const printCom = useRef();
    const autoReloadTimer = useRef();
    const quickReloadTimeOut = useRef();

    const header = useRef();
    const footer = useRef();
    const tabs = useRef();
    const bottomActions = useRef();
    const msgBox = useRef();
    const mainEditPopup = useRef();

    const isRenderPage = useMemo(() => AntCondition.isRender(mainData, props), [mainData, props]);
    const isVisiblePage = Boolean(mainData && mainID.current);
    const isVisibleHeader = useMemo(() => props.header && AntCondition.isVisible(mainData, props.header), [mainData, props.header]);
    const isVisibleFooter = useMemo(() => props.footer && AntCondition.isVisible(mainData, props.footer), [mainData, props.footer]);

    const isInValidData = useMemo(() => (mainID.current || mainCode.current) && mainData && !mainID.current, [mainData]);
    const isPermDeniedData = useMemo(() => (mainID.current || mainCode.current) && mainData?.errorCode === cs.erPermissionDenied.code, [mainData]);

    const renderLoading = isRenderPage && !isPermDeniedData && mainState.loading;
    const renderBootSkeleton = Boolean(renderLoading && bootCode && !mainState.error);

    // Chốt an toàn: máy chủ không trả sau 20 giây thì bỏ khung xám, tránh treo mãi.
    useEffect(() => {
        if (!bootCode) return;
        var timer = setTimeout(() => setBootCode(null), 20000);
        return () => clearTimeout(timer);
    }, [bootCode]);
    const renderAccessDenied = !isRenderPage || isPermDeniedData;
    const renderNotExistData = isRenderPage && !isPermDeniedData && !renderLoading && isPrepared.current && isInValidData;

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);

    const showAlert = useCallback((args, onClosedCallback) => {
        msgBox.current?.show(args, onClosedCallback);
    }, []);

    const setError = useCallback(
        (content, errors) => {
            if (!content && !mainState.error) return;

            let errorData = errors;
            if (errorData === 'TypeError: Failed to fetch') errorData = FETCH_ERROR_MSG;

            const values = !errorData ? [] : Array.isArray(errorData) ? errorData : [errorData];
            const finalContent = values.length === 1 ? values[0] : content || values[0];

            setMainState((v) => ({
                ...v,
                error: finalContent ? { text: String(finalContent), values } : null
            }));

            if (props.onError) props.onError(finalContent, errorData);
        },
        [mainState.error, props.onError]
    );

    const showPopup = useCallback(
        (refTarget, withMain, defaultObj) => {
            const target = refTarget?.current || refTarget;
            if (!target?.show) return;

            let obj = { ...defaultObj };
            if (withMain && (mainID.current || mainCode.current)) {
                obj = { ...mainData, ...obj };
            }
            target.show(obj);
        },
        [mainData]
    );

    const navigateToReload = useCallback(
        (id, code) => {
            if (quickReloadTimeOut.current) clearTimeout(quickReloadTimeOut.current);
            if (autoReloadTimer.current) clearInterval(autoReloadTimer.current);
            props.onNavigateToReload?.({ id, code });
        },
        [props.onNavigateToReload]
    );

    const onActionQuickCreate = useCallback(
        async (done) => {
            const result = await props.API.postQuickCreate();
            done?.();
            if (result?.error) showAlert({ content: result.error });
            else if (result?.data) navigateToReload(result.data.id, result.data.code);
        },
        [props.API, showAlert, navigateToReload]
    );

    const onActionClone = useCallback(async () => {
        const result = await props.API.postClone(mainCode.current, mainID.current);
        if (result?.error) showAlert({ content: result.error });
        else if (result?.data) navigateToReload(result.data.id, result.data.code);
    }, [props.API, showAlert, navigateToReload]);

    const onAction = useCallback(
        async (btn) => {
            if (props.onBeforeAction) await props.onBeforeAction(btn?.name);
            if (props.onAction) {
                const result = await props.onAction(btn);
                if (props.onAfterAction) await props.onAfterAction(btn?.name, result);
                return result;
            }
        },
        [props.onBeforeAction, props.onAfterAction, props.onAction]
    );

    const handlePrint = useReactToPrint({
        content: () => props.printComponent?.current || printCom.current
    });

    const validate = useCallback(async () => {
        let isOK = true;
        if (header.current && !(await header.current.validate())) isOK = false;
        if (footer.current && !(await footer.current.validate())) isOK = false;
        if (tabs.current && !(await tabs.current.validate())) isOK = false;
        return isOK;
    }, []);

    const getValidData = useCallback(
        async (byPassValidate) => {
            if (!byPassValidate && !(await validate())) return null;

            const obj = {};
            const skipFields = ['actions', 'edit', 'infos', 'checksum', 'recalc_checksum', 'updated_at'];

            Object.entries(mainData).forEach(([fieldName, objCurrent]) => {
                if (skipFields.includes(fieldName)) return;

                const objOld = mainState.oldData?.[fieldName];
                if (objCurrent !== objOld && stringify(objCurrent) !== stringify(objOld)) {
                    if ((objCurrent !== '' && objCurrent !== null) || (objOld !== '' && objOld !== null)) {
                        obj[fieldName] = objCurrent;
                    }
                }
            });
            return obj;
        },
        [validate, mainData, mainState.oldData]
    );

    const loadContent = useCallback(
        async (opts) => {
            if (!props.API?.postUpdatePrepare) return;
            if (opts?.isRefresh && quickReloadTimeOut.current) {
                clearTimeout(quickReloadTimeOut.current);
                quickReloadTimeOut.current = null;
            }

            const id = props.id;
            const code = props.code;
            if (!id && !code) return;

            if (opts?.changedFields && props.refreshOnChangedFields && !opts.changedFields.some((f) => props.refreshOnChangedFields.includes(f)))
                return;

            loadSeqRef.current += 1;
            summarySeqRef.current += 1;
            const loadSeq = loadSeqRef.current;

            // Smart refresh: khi isRefresh + co refreshSummaryFields + dieu kien match → chi cap nhat summary fields
            let currentData = mainDataRef.current;
            if (
                opts?.isRefresh &&
                !opts?.bypassSnapshot &&
                props.refreshSummaryFields &&
                currentData &&
                (!props.refreshSummaryWhen || props.refreshSummaryWhen(currentData))
            ) {
                try {
                    const prepareOpts = { ...props.prepareConditions, ...props.header?.prepareConditions, ...(props.isSetting && { isSetting: true }) };
                    let rs = await props.API.postUpdatePrepare(id, code, false, prepareOpts, mainDataRef.current);
                    if (!rs || rs.error) return rs;
                    if (loadSeq !== loadSeqRef.current) return rs;
                    if (rs.updated_at && dataVersionRef.current && rs.updated_at < dataVersionRef.current) return rs;
                    if (rs.updated_at) dataVersionRef.current = rs.updated_at;

                    let changes = {};
                    for (let f of props.refreshSummaryFields) {
                        if (rs[f] !== undefined && stringify(currentData[f]) !== stringify(rs[f])) {
                            changes[f] = rs[f];
                        }
                    }

                    // Kiem tra xem co field NGOAI summary thay doi khong
                    let hasNonSummaryChange = false;
                    let summarySet = new Set(props.refreshSummaryFields);
                    for (let key in rs) {
                        if (!summarySet.has(key) && rs[key] !== undefined && key !== 'edit' && stringify(currentData[key]) !== stringify(rs[key])) {
                            hasNonSummaryChange = true;
                            break;
                        }
                    }

                    // Neu chi co summary fields thay doi → setMainData chi voi changes
                    // Grid khong reload vi parentData?.id khong doi
                    if (!hasNonSummaryChange) {
                        if (Object.keys(changes).length > 0) {
                            // Chi update footerData, khong dong mainData
                            for (let f in changes) {
                                mainDataRef.current[f] = changes[f];
                            }
                            setFooterData((prev) => ({ ...prev, ...changes }));
                        }
                        return rs;
                    }
                    // Neu co non-summary field thay doi → fall through to full reload below
                } catch (e) {
                    console.error('[loadContent smart refresh]', e);
                }
            }

            let obj = opts?.newObj || null;
            if (!opts?.isRefresh) isPrepared.current = false;

            var isOtherVoucher = Boolean(code) && code !== mainCode.current;
            if (isOtherVoucher && !opts?.isRefresh) {
                dataVersionRef.current = null;
                setBootCode(String(code));
            }

            const prepareOpts = {
                ...props.prepareConditions,
                ...props.header?.prepareConditions,
                ...(opts?.forcePrepare && { forcePrepare: true }),
                ...(props.isSetting && { isSetting: true })
            };

            setMainState((v) => {
                if (opts?.isRefresh) return v.error ? { ...v, error: null } : v;
                return {
                    ...v,
                    error: null,
                    loading: true,
                    oldData: obj ? JSON.parse(stringify(obj)) : null
                };
            });

            obj = await props.API.postUpdatePrepare(
                id,
                code,
                Boolean(opts?.recalcable && typeof obj === 'object'),
                prepareOpts,
                opts?.bypassSnapshot ? undefined : mainDataRef.current
            );

            if (loadSeq !== loadSeqRef.current) return obj;
            if (obj?.error) {
                setBootCode(null);
                setMainState((v) => ({ ...v, error: obj.error, loading: false }));
                return obj;
            }

            if (obj) {
                if (obj.updated_at && dataVersionRef.current && obj.updated_at < dataVersionRef.current) {
                    setBootCode(null);
                    setMainState((v) => ({ ...v, error: null, loading: false }));
                    return obj;
                }
                applyPreparedData(obj);
            }

            setBootCode(null);
            setMainState((v) => {
                const oldData = obj ? JSON.parse(stringify(obj)) : null;
                if (!v.error && !v.loading && stringify(v.oldData) === stringify(oldData)) return v;
                return { ...v, error: null, loading: false, oldData: oldData };
            });
            return obj;
        },
        [props, props.API, props.refreshOnChangedFields, props.prepareConditions, props.isSetting, applyPreparedData]
    );

    const saveOnFieldDataChanged = useCallback(async () => {
        if (isSaving.current) return;
        setError(null);

        let newObj = await getValidData(true);
        if (!newObj || Object.keys(newObj).length === 0) return;

        if (props.onSaving) newObj = props.onSaving(mainData, newObj);

        isSaving.current = true;
        const opts = { getNewPrepare: true, ...(props.isSetting && { isSetting: true }) };
        const whereObj = { id: mainID.current, checksum: mainData.checksum, ...(props.isSetting && { isSetting: true }) };

        const result = await props.API.postUpdate(whereObj, newObj, opts, mainDataRef.current);
        if (!result?.data?.updated) {
            setError('Không thể cập nhật thông tin !', mainConfig.translateFirstError(result));
        } else {
            await loadContent({ changedFields: Object.keys(newObj), newObj: result.data });
            props.onSaved?.(result.data);
        }
        isSaving.current = false;
    }, [setError, getValidData, mainData, mainConfig, loadContent, props]);

    const onFieldDataChanged = useCallback(
        async (e) => {
            if (e.code) return navigateToReload(null, e.code);
            if (e.id) return navigateToReload(e.id, null);

            if (props.onFieldDataChanged && !props.onFieldDataChanged(e)) return;
            await saveOnFieldDataChanged();
        },
        [navigateToReload, props.onFieldDataChanged, saveOnFieldDataChanged]
    );

    const loadWhenHasNewData = useCallback(async () => {
        const id = props.id;
        const code = props.code;
        if (!id && !code) return false;

        let hadNew = false;
        try {
            if (header.current) hadNew = await header.current.isHadNewData(id, code);
        } catch (error) {}
        if (hadNew) await loadContent();
        return hadNew;
    }, [props.id, props.code, loadContent]);

    const autoSmartReload = useCallback(
        async (loadNow, callTime = 0) => {
            if (callTime > 5) return;
            if (quickReloadTimeOut.current) clearTimeout(quickReloadTimeOut.current);

            const hadNew = loadNow ? (await loadContent(), true) : await loadWhenHasNewData();

            quickReloadTimeOut.current = setTimeout(
                () => {
                    autoSmartReload(false, callTime + 1);
                },
                hadNew ? 5000 : 15000
            );
        },
        [loadContent, loadWhenHasNewData]
    );

    const reload = useCallback(
        async (opts) => {
            await loadContent(opts);
        },
        [loadContent]
    );

    const load = useCallback(
        async (data) => {
            const id = data?.id || props.id || mainID.current;
            const code = data?.code || props.code || mainCode.current;
            if (id || code) await loadContent({ recalcable: true });
        },
        [props.id, props.code, loadContent]
    );

    const forceReload = useCallback(
        (data) => {
            if (quickReloadTimeOut.current) clearTimeout(quickReloadTimeOut.current);
            if (autoReloadTimer.current) clearInterval(autoReloadTimer.current);

            const nextCode = data?.code || props.code;
            const nextID = data?.id || props.id;
            if ((nextCode && nextCode !== mainCode.current) || (nextID && String(nextID) !== String(mainID.current))) {
                dataVersionRef.current = null;
                loadSeqRef.current += 1;
                summarySeqRef.current += 1;
            }
            mainCode.current = nextCode;
            mainID.current = nextID;

            // Khi parent đóng/xoá phiếu, chỉ ẩn voucher là chưa đủ: dữ liệu, mã và id
            // cũ còn lưu trong component sẽ làm lần mở phiếu tiếp theo đi theo luồng cũ.
            // Reset đồng bộ để lượt chọn/tạo đơn kế tiếp luôn nạp voucher mới.
            if (!mainCode.current && !mainID.current) {
                loadSeqRef.current += 1;
                summarySeqRef.current += 1;
                dataVersionRef.current = null;
                isPrepared.current = false;
                setAllData({});
                setMainState((state) => ({ ...state, error: null, loading: false, oldData: null }));
                setBootCode(null);
                forceRender();
                return;
            }

            load(data);
            forceRender();
        },
        [load, props.code, props.id, forceRender, setAllData]
    );

    // const autoReload = useCallback(
    //     (loadNow) => {
    //         if (loadNow) forceReload();

    //         if (autoReloadTimer.current) clearInterval(autoReloadTimer.current);
    //         autoReloadTimer.current = setInterval(() => {
    //             reload();
    //         }, 30 * 1000);
    //     },
    //     [reload,forceReload]
    // );

    const postAction = useCallback(
        async (action, code = mainCode.current, id = mainID.current) => {
            if (!id && !code) return;

            if (quickReloadTimeOut.current) clearTimeout(quickReloadTimeOut.current);
            if (autoReloadTimer.current) clearInterval(autoReloadTimer.current);

            const useQuickResponse = session.isSocketConnected();
            // Gửi kèm ngữ cảnh prepare: tuyến khai `updatePrepare: true` trả thẳng phiếu đầy đủ,
            // khỏi phải nạp lại phiếu thêm một lượt sau hành động. Màn có điều kiện prepare riêng
            // thì không đi đường tắt (phiếu trong phản hồi dựng không kèm điều kiện đó).
            const hasPrepareConditions = Boolean(
                (props.prepareConditions && Object.keys(props.prepareConditions).length > 0) ||
                    (props.header?.prepareConditions && Object.keys(props.header.prepareConditions).length > 0)
            );
            const actionPrepareOpts = props.isSetting ? { isSetting: true } : undefined;
            const result = await props.API.postAction(action, code, id, useQuickResponse, mainDataRef.current, actionPrepareOpts);

            if (result?.error) {
                showAlert({ content: result.error });
            } else {
                props.onActionPosted?.(action, result);

                if (action === 'close') {
                    if (mainCode.current === code || mainID.current === id) await forceReload();
                    return result;
                }
                if (action === 'print') return result?.data;

                if (!result?.data?.isQuickResponse || !useQuickResponse) {
                    if (mainCode.current === code || mainID.current === id) {
                        // Phiếu đầy đủ đã nằm sẵn trong phản hồi thì áp thẳng; chỉ khi không
                        // phải phiếu đầy đủ mới nạp lại như trước.
                        const applied =
                            !hasPrepareConditions && result?.data && result.data.actions instanceof Array && result.data.edit
                                ? applyPreparedData(result.data)
                                : false;
                        if (!applied) await forceReload();
                    }
                } else if (result?.data) {
                    // Quick response chỉ là trạng thái tức thời, không phải prepared
                    // voucher. Giữ actions/edit hiện tại cho tới khi snapshot socket hoặc
                    // updatePrepare xác nhận bản cuối, tránh toolbar biến mất rồi hiện lại.
                    summarySeqRef.current += 1;
                    const isStaleQuickResponse =
                        result.data.updated_at && dataVersionRef.current && result.data.updated_at <= dataVersionRef.current;
                    if (!isStaleQuickResponse) {
                        setAllData((v) => {
                            const next = { ...v, ...result.data };
                            if (v.actions) next.actions = v.actions;
                            if (v.edit) next.edit = v.edit;
                            mainDataRef.current = next;
                            if (next.updated_at) dataVersionRef.current = next.updated_at;
                            return next;
                        });
                    }
                    // Safety net khi socket đang báo connected nhưng packet hoàn tất bị
                    // thất lạc: vẫn đối chiếu đúng một lần, không để voucher kẹt ở bản
                    // quick response. Socket tới bình thường sẽ thay lượt này bằng debounce 350ms.
                    quickReloadTimeOut.current = setTimeout(() => {
                        quickReloadTimeOut.current = null;
                        reload({ isRefresh: true });
                    }, 1000);
                }
            }
            return result;
        },
        [
            props.API,
            props.onActionPosted,
            props.prepareConditions,
            props.header?.prepareConditions,
            props.isSetting,
            forceReload,
            reload,
            session,
            showAlert,
            applyPreparedData
        ]
    );

    const onActionCancel = useCallback(async () => {
        await props.onBeforeAction?.('cancel');
        const result = await postAction('cancel');
        await props.onAfterAction?.('cancel', result);
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionDelete = useCallback(async () => {
        await props.onBeforeAction?.('delete');
        const result = await postAction('delete');
        if (!result?.error) await props.onAfterAction?.('delete', result);
        return result;
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionRelease = useCallback(async () => {
        if ((await props.onBeforeAction?.('release')) === false) return;
        const result = await postAction('release');
        await props.onAfterAction?.('release', result);
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionClose = useCallback(async () => {
        await props.onBeforeAction?.('close');
        const result = await postAction('close');
        await props.onAfterAction?.('close', result);
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionPost = useCallback(async () => {
        await props.onBeforeAction?.('post');
        const result = await postAction('post');
        await props.onAfterAction?.('post', result);
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionApprove = useCallback(async () => {
        await props.onBeforeAction?.('approve');
        const result = await postAction('approve');
        await props.onAfterAction?.('approve', result);
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionReject = useCallback(async () => {
        await props.onBeforeAction?.('reject');
        const result = await postAction('reject');
        await props.onAfterAction?.('reject', result);
    }, [props.onBeforeAction, props.onAfterAction, postAction]);

    const onActionPrint = useCallback(
        async (strTemplate, options = {}) => {
            const preparedPrinter = props.printComponent?.current || printCom.current;
            if (options.preparedData?.html && typeof preparedPrinter?.load === 'function') {
                await preparedPrinter.load(options.preparedData);
                return true;
            }
            if (props.printComponent && props.printComponent.current && typeof props.printComponent.current.load === 'function') {
                var data = await postAction('print');
                await props.printComponent.current.load(data);
            } else if (props.printUrl && printCom.current) {
                if (props.service === 'AROrder') {
                    var delivery = await props.API.postAsync('/ar/AROrder/remotePrint', {
                        code: props?.code,
                        id: props?.id,
                        template: typeof strTemplate === 'string' ? strTemplate : undefined
                    });
                    var deliveryData = delivery && delivery.data ? delivery.data : delivery;
                    if (deliveryData?.dispatched === true) return true;
                    if (deliveryData?.print_data?.html) {
                        await printCom.current.load(deliveryData.print_data);
                        return true;
                    }
                    // The delivery API owns fallback. Never bypass its error or
                    // permission result with a second generateBillHTML request.
                    return false;
                }
                var body = {
                    code: props && props.code ? props.code : undefined,
                    id: props && props.id ? props.id : undefined,
                    template: strTemplate
                };

                var data = await props.API.postAsync(props.printUrl, body);
                if (data && !data.error) printCom.current.load(data.data);

                // props.API.post(props.printUrl, body, (data) => {
                //     printCom.current.load(data.data);
                // });
            }
            return false;
        },
        [props.printComponent, props.printUrl, props.API, props.service, postAction]
    );

    const onActionRecalculate = useCallback(async () => {
        await postAction('recalculate');
    }, [postAction]);

    const onPrintClick = useCallback(() => {
        onActionPrint();
    }, [onActionPrint]);

    const onRecalculateClick = useCallback(() => {
        onActionRecalculate();
    }, [onActionRecalculate]);

    const showMainEditPopup = useCallback(async () => {
        if (mainEditPopup.current) {
            let mainID = mainData?.id;
            if (mainID) {
                let defaultObj = await mainEditPopup.current.showById(mainID);
                if (props.editMain && props.editMain.onEditPrepared) props.editMain.onEditPrepared(defaultObj);
            }
        }
    }, [props.editMain, mainData]);

    const onInfoClick = useCallback(() => {
        showMainEditPopup();
    }, [showMainEditPopup]);

    useEffect(() => {
        mainCode.current = null;
        AntAuth.checkValidSession();

        const hasPrintListener = props.onPrinted && props.toolbar?.showPrint && (props.printComponent?.current || props.printUrl);
        if (hasPrintListener) window.addEventListener('afterprint', props.onPrinted);

        return () => {
            if (autoReloadTimer.current) clearInterval(autoReloadTimer.current);
            if (hasPrintListener) window.removeEventListener('afterprint', props.onPrinted);

            mainCode.current = null;
            mainID.current = null;
            header.current = null;
            footer.current = null;
            tabs.current = null;
            msgBox.current = null;
        };
    }, []);

    const forceReloadRef = useRef(forceReload);
    forceReloadRef.current = forceReload;

    useEffect(() => {
        forceReloadRef.current(props.mainData);
    }, [props.mainData]);

    const reloadTabGrid = useCallback(
        (name) => {
            let gridName = name;
            if (!gridName && Array.isArray(props.tabs) && props.tabs.length > 0) {
                const firstTab = props.tabs.find((t) => t.grid);
                if (firstTab) {
                    gridName = firstTab.grid?.name || firstTab.name;
                }
            }

            if (gridName && tabs.current?.reloadGrid) {
                tabs.current.reloadGrid(gridName);
            }
        },
        [props.tabs]
    );

    const refreshSummary = useCallback(
        async (opts) => {
            if (!props.refreshSummaryFields || !props.API?.postUpdatePrepare) return;
            const id = props.id;
            const code = props.code;
            if (!id && !code) return;

            let currentData = mainDataRef.current;
            if (!currentData) return;
            summarySeqRef.current += 1;
            const summarySeq = summarySeqRef.current;

            // Fast-path: payload socket đã mang sẵn totals (BE đính vào *-Recalculated)
            // → áp NGAY cho footer (0 chờ mạng). KHÔNG return: vẫn chạy tiếp API bên dưới
            // để lấy ACTIONS mới (actionRelease chỉ có sau khi BE tính — payload không mang
            // actions; return sớm từng làm đơn mới thêm SP bị mất bottom actions).
            if (opts?.payload?.totals && Object.keys(opts.payload.totals).length > 0) {
                if (!(opts.payload.updated_at && currentData.updated_at && opts.payload.updated_at < currentData.updated_at)) {
                    let pChanges = {};
                    for (let f of props.refreshSummaryFields) {
                        if (opts.payload.totals[f] !== undefined && stringify(currentData[f]) !== stringify(opts.payload.totals[f])) {
                            pChanges[f] = opts.payload.totals[f];
                        }
                    }
                    if (opts.payload.updated_at) {
                        mainDataRef.current.updated_at = opts.payload.updated_at;
                        dataVersionRef.current = opts.payload.updated_at;
                    }
                    if (Object.keys(pChanges).length > 0) {
                        for (let f in pChanges) {
                            mainDataRef.current[f] = pChanges[f];
                        }
                        if (bottomActions.current?.refreshActions) bottomActions.current.refreshActions();
                        if (footer.current?.forceRender) footer.current.forceRender();
                    }
                }
            }

            // Socket payload chỉ cập nhật tức thời phần summary. Việc gọi
            // actionUpdatePrepare được AntPOSScreen gom bằng một trailing debounce,
            // tránh mỗi event Recalculated tạo thêm một request riêng.
            if (opts?.skipPrepare) return;

            try {
                const prepareOpts = { ...props.prepareConditions, ...props.header?.prepareConditions, ...(props.isSetting && { isSetting: true }) };
                let rs = await props.API.postUpdatePrepare(id, code, false, prepareOpts, mainDataRef.current);
                if (!rs || rs.error) return;
                if (summarySeq !== summarySeqRef.current) return;
                if (rs.updated_at && dataVersionRef.current && rs.updated_at < dataVersionRef.current) return;
                if (rs.updated_at) dataVersionRef.current = rs.updated_at;

                let changes = {};
                for (let f of props.refreshSummaryFields) {
                    if (rs[f] !== undefined && stringify(currentData[f]) !== stringify(rs[f])) {
                        changes[f] = rs[f];
                    }
                }

                if (Object.keys(changes).length === 0) return;

                // Mutate ref in-place, KHONG goi setState → grid KHONG re-render
                for (let f in changes) {
                    mainDataRef.current[f] = changes[f];
                }

                // Update footer + actions truc tiep qua imperative handles
                if (bottomActions.current?.refreshActions) bottomActions.current.refreshActions();
                if (footer.current?.forceRender) footer.current.forceRender();
            } catch (e) {
                console.error('[refreshSummary]', e);
            }
        },
        [props.refreshSummaryFields, props.API, props.id, props.code, props.prepareConditions, props.header?.prepareConditions, props.isSetting]
    );

    useImperativeHandle(ref, () => ({
        showPopup,
        showAlert,
        postAction,
        load: forceReload,
        reload: reload,
        applyPreparedData: applyPreparedData,
        refreshSummary: refreshSummary,
        autoReload: () => autoSmartReload(true),
        getFormData: () => mainData,
        runPrint: onActionPrint,
        actionQuickCreate: onActionQuickCreate,
        reloadTabGrid: reloadTabGrid
    }));

    const memoizedTabs = useMemo(
        () =>
            props.tabs instanceof Array && props.tabs.length > 0 ? (
                <BoxItem ratio={1}>
                    <div
                        style={{
                            visibility: isVisiblePage ? 'visible' : 'hidden',
                            marginTop: '10px',
                            marginBottom: '5px',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        <AntVoucherTabs
                            ref={tabs}
                            shouldRender={isRender}
                            API={props.API}
                            formData={mainData}
                            formTable={mainTable}
                            oldData={mainState.oldData}
                            tabs={props.tabs}
                            tabStyle={mainState.tabStyle}
                            autoExpandAll={props.tabAutoExpandAll}
                            groupCollapsible={props.tabCollapsible}
                            groupExpandMultiple={props.tabExpandMultiple}
                            autoHideTitle={props.tabAutoHideTitle}
                            onFieldDataChanged={onFieldDataChanged}
                            onDataSourceEdited={props.refreshSummaryFields ? undefined : forceReload}
                            onAfterAction={props.refreshSummaryFields ? undefined : forceReload}
                            onError={setError}
                            visible={isVisiblePage}
                            disabled={mainState.toolbarProcessingButton ? true : undefined}
                        />
                    </div>
                </BoxItem>
            ) : null,
        [
            props.tabs,
            isVisiblePage,
            isRender,
            props.API,
            mainData,
            mainTable,
            mainState.oldData,
            mainState.tabStyle,
            mainState.toolbarProcessingButton,
            props.tabAutoExpandAll,
            props.tabCollapsible,
            props.tabExpandMultiple,
            props.tabAutoHideTitle,
            onFieldDataChanged,
            props.refreshSummaryFields,
            forceReload,
            setError
        ]
    );

    const onRefreshClick = useCallback(async () => {
        await loadContent({ recalcable: true, isRefresh: true, forcePrepare: true, bypassSnapshot: true });
        requestAnimationFrame(() => tabs.current?.reload?.({ forceQuery: true, byPassGetCache: true }));
    }, [loadContent]);

    const onActionProcessing = useCallback(
        (btn) => {
            if (btn?.name === 'showSwitchUI') {
                const uiData = JSON.parse(localStorage.getItem(`${cs.System_Code}-ui-states`) || '{}');
                localStorage.setItem(`${cs.System_Code}-ui-states`, stringify({ ...uiData, mode: 'WebUI' }));

                AntAuth.refreshToken(false, null, () => {
                    navigate(mainData.code ? `${props.switchModeURL}/${mainData.code}` : '/home');
                    window.location.reload();
                });
            }
            props.onActionProcessing?.(btn);
            setMainState((v) => ({ ...v, toolbarProcessingButton: btn?.name }));
        },
        [mainData.code, props.onActionProcessing, props.switchModeURL, navigate]
    );

    const onActionProceeed = useCallback(() => {
        props.onActionProceeed?.();
        setMainState((v) => ({ ...v, toolbarProcessingButton: null }));
    }, [props.onActionProceeed]);

    return (
        <AntGridScope segment={buildGridScopeSegment('pos', mainTable)}>
            {isRender && (
                <AntPage
                    className={props.className}
                    marginLeft={5}
                    marginRight={5}
                    marginBottom={props.marginBottom ? props.marginBottom : 5}
                    visible={Boolean(props.mainData && mainData)}
                    backgroundImage={props.backgroundImage}
                    showContent={Boolean(mainCode.current || bootCode)}
                    hideBackgroundImage={Boolean(mainCode.current || bootCode) && (renderLoading || renderAccessDenied || renderNotExistData)}
                >
                    {/* <ReactToPrint content={props.printComponent}>
                        <PrintContextConsumer>
                            {({ runPrint }) => (
                                
                            )}
                        </PrintContextConsumer>
                    </ReactToPrint> */}

                    {isRenderPage && !isPermDeniedData && !renderLoading && mainData && (
                        <AntToolbar
                            API={props.API}
                            table={props.table}
                            shouldRender={isRender}
                            parentData={footerData}
                            statusField={props.statusField}
                            renderParent={Boolean(isRenderPage && !isInValidData && isPrepared.current)}
                            // renderParent={isRenderPage && !isInValidData && isPrepared.current}
                            title={props.title ? AntLocalization.translateText(props.title) : AntLocalization.translateText(mainEntityDisplay)}
                            titleIcon={props.titleIcon}
                            processingButton={mainState.toolbarProcessingButton}
                            showBack={true}
                            showHome={true}
                            showHelp={props.toolbar ? props.toolbar.showHelp : false}
                            showRefresh={true}
                            showInfo={Boolean(props.editMain && props.editMain.items instanceof Array)}
                            showVoucherCode={Boolean(props.toolbar?.showVoucherCode)}
                            showSwitchUI={Boolean(props.switchModeURL)}
                            showPrint={props.toolbar ? props.toolbar.showPrint : false}
                            showRecalculate={props.toolbar?.showRecalculate !== undefined ? props.toolbar.showRecalculate : true}
                            showActions={props.toolbar ? props.toolbar.showActions : undefined}
                            // showActions={false}
                            actionPack={props.toolbar ? props.toolbar.actionPack : undefined}
                            items={props.toolbar ? props.toolbar.items : undefined}
                            actions={props.toolbar ? props.toolbar.actions : undefined}
                            onRefreshClick={onRefreshClick}
                            onPrintClick={onPrintClick}
                            onRecalculateClick={onRecalculateClick}
                            onInfoClick={onInfoClick}
                            onActionQuickCreate={onActionQuickCreate}
                            onActionClone={onActionClone}
                            onActionDelete={onActionDelete}
                            onAction={onAction}
                            onActionProcessing={onActionProcessing}
                            onActionProceeed={onActionProceeed}
                            actionRelease={props.actionRelease}
                            actionPost={props.actionPost}
                            onActionCancel={onActionCancel}
                            onActionRelease={onActionRelease}
                            onActionClose={onActionClose}
                            onActionPost={onActionPost}
                            onActionApprove={onActionApprove}
                            onActionReject={onActionReject}
                        />
                    )}

                    {renderAccessDenied && (
                        <AntErrorPage
                            id={`ant-voucher-error-page-${props.API.entityName}`}
                            text={AntLocalization.translateText({
                                vi: 'Không có quyền truy cập !',
                                en: 'Access Denied',
                                fr: 'Accès refusé',
                                es: 'Acceso denegado',
                                id: 'Akses ditolak',
                                ja: 'アクセス拒否',
                                zh: '访问被拒绝',
                                ko: '접근 거부',
                                hi: 'पहुँच अस्वीकृत',
                                ar: 'تم رفض الوصول'
                            })}
                        />
                    )}
                    {renderBootSkeleton && (
                        <AntVoucherSkeleton
                            showHeader={Boolean(props.header && props.header.items instanceof Array && props.header.items.length > 0)}
                            headerRows={2}
                            tabs={props.tabs instanceof Array && props.tabs.length > 0 ? props.tabs.length : 3}
                            rows={5}
                        />
                    )}
                    {renderLoading && !renderBootSkeleton && (
                        <div>
                            <LoadPanel
                                position={{ of: '#ant-pos-screen-main-id' }}
                                visible={true}
                                showPane={false}
                                shading={true}
                                // shadingColor={'rgba(119, 119, 128, 0.16)'}
                                wrapperAttr={{
                                    class: 'ant-loading'
                                }}
                                // indicatorSrc={'https://image.flaticon.com/icons/svg/3305/3305803.svg'}
                            />
                        </div>
                    )}

                    {renderNotExistData && (
                        <AntErrorPage
                            errors={mainState.error ? mainState.error.values : undefined}
                            id={`ant-voucher-error-page-${props.API.entityName}`}
                            text={
                                mainState.error
                                    ? AntLocalization.translateText(mainState.error.text)
                                    : {
                                          vi: 'Dữ liệu không tồn tại !',
                                          en: 'Data not found !',
                                          fr: 'Données introuvables !',
                                          es: '¡Datos no encontrados!',
                                          id: 'Data tidak ditemukan !',
                                          ja: 'データが存在しません！',
                                          zh: '数据不存在！',
                                          ko: '데이터가 없습니다!',
                                          hi: 'डेटा मौजूद नहीं है !',
                                          ar: 'البيانات غير موجودة !'
                                      }
                            }
                        />
                    )}

                    {isRenderPage && !isPermDeniedData && !renderNotExistData && !renderLoading && Boolean(mainCode.current) && (
                        // <div style={{ visibility: isVisiblePage ? 'visible' : 'hidden' }}>
                        <div
                            style={{
                                display: isVisiblePage && isPrepared.current && props.mainData && mainData ? 'flex' : 'none',
                                flexDirection: 'column',
                                height: '100%'
                                // maxHeight: '500px'
                            }}
                        >
                            <Box direction="col" width="100%" height="100%">
                                <BoxItem baseSize={'auto'}>
                                    {isVisibleHeader && (
                                        <AntBaseForm
                                            ref={header}
                                            shouldRender={isRender}
                                            API={props.API}
                                            type={'Update'}
                                            formData={mainData}
                                            formTable={mainTable}
                                            oldData={mainState.oldData}
                                            table={props.header && props.header.table ? props.header.table : props.table}
                                            alignItemLabelsInAllGroups={props.header ? props.header.alignItemLabelsInAllGroups : undefined}
                                            labelLocation={props.header ? props.header.labelLocation : undefined}
                                            colCount={props.header && props.header.colCount !== undefined ? props.header.colCount : 1}
                                            colCountDefault={props.header ? props.header.colCountDefault : undefined}
                                            fields={props.header && props.header.fields ? props.header.fields : []}
                                            items={props.header && props.header.items ? props.header.items : []}
                                            postFields={props.header ? props.header.postFields : undefined}
                                            onFieldDataChanged={onFieldDataChanged}
                                            prepareConditions={props.header ? props.header.prepareConditions : undefined}
                                            underlined={true}
                                            hideBorder={true}
                                            onError={setError}
                                            visible={isVisiblePage}
                                            isSearching={props.header ? props.header.isSearching : undefined}
                                            disabled={mainState.toolbarProcessingButton ? true : props.header ? props.header.disabled : undefined}
                                        />
                                    )}
                                    {/* {isVisibleHeader && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />} */}
                                    {mainState.error && (
                                        <div className={'ant-voucher-error-container'}>
                                            <AntErrorPanel
                                                errors={mainState.error.values}
                                                id={`ant-voucher-error-${props.API.entityName}`}
                                                text={mainState.error.text}
                                                hasBackground={true}
                                            />
                                            {isVisibleHeader && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                        </div>
                                    )}
                                    {mainInfos.length > 0 && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                    {mainInfos.map((info, i) => {
                                        if (info.type === 'Warning' || info.type === 'Error')
                                            return (
                                                <div key={'ant-voucher-error-container-' + i} className={'ant-voucher-error-container'}>
                                                    <AntErrorPanel
                                                        id={`ant-voucher-error-${props.API.entityName}` + '-' + i}
                                                        text={info.message}
                                                        hasBackground={true}
                                                    />
                                                    {<p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                                </div>
                                            );

                                        return (
                                            <div className={'ant-voucher-info-container'}>
                                                <AntInfoPanel
                                                    id={`ant-voucher-info-${props.API.entityName}` + '-' + i}
                                                    text={info.message}
                                                    showYes={info.agreeable}
                                                    onAgreeClick={(e, done) => {
                                                        if (props.onInfoAgree) props.onInfoAgree(info, done);
                                                    }}
                                                />
                                                {<p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                            </div>
                                        );
                                    })}
                                </BoxItem>
                                {memoizedTabs}
                                {(props.tabs instanceof Array === false || props.tabs.length <= 0) && <BoxItem baseSize={40} />}
                                <BoxItem baseSize={'auto'}>
                                    {/* {isVisibleFooter && props.tabs.length > 0 && <p style={{ marginBlockStart: '0px', marginBlockEnd: '50px' }} />} */}
                                    {isVisibleFooter && (
                                        <AntBaseForm
                                            ref={footer}
                                            shouldRender={isRender}
                                            API={props.API}
                                            type={'Update'}
                                            formData={footerData}
                                            formTable={mainTable}
                                            oldData={mainState.oldData}
                                            table={props.footer && props.footer.table ? props.footer.table : props.table}
                                            alignItemLabelsInAllGroups={props.footer ? props.footer.alignItemLabelsInAllGroups : undefined}
                                            labelLocation={props.footer ? props.footer.labelLocation : undefined}
                                            colCount={props.footer && props.footer.colCount !== undefined ? props.footer.colCount : 1}
                                            colCountDefault={props.footer ? props.footer.colCountDefault : undefined}
                                            fields={props.footer && props.footer.fields !== undefined ? props.footer.fields : []}
                                            items={props.footer && props.footer.items !== undefined ? props.footer.items : []}
                                            postFields={props.footer ? props.footer.postFields : undefined}
                                            onFieldDataChanged={onFieldDataChanged}
                                            onError={setError}
                                            underlined={true}
                                            hideBorder={true}
                                            visible={isVisiblePage}
                                            isSearching={props.footer ? props.footer.isSearching : undefined}
                                            disabled={mainState.toolbarProcessingButton ? true : props.footer ? props.footer.disabled : undefined}
                                        />
                                    )}
                                    <AntPOSVoucherActions
                                        ref={bottomActions}
                                        API={props.API}
                                        table={props.table}
                                        shouldRender={isRender}
                                        parentData={footerData}
                                        renderParent={Boolean(isRenderPage && !isInValidData && isPrepared.current)}
                                        // renderParent={isRenderPage && !isInValidData && isPrepared.current}
                                        processingButton={mainState.toolbarProcessingButton}
                                        // showPrint={false}
                                        showActions={props.bottomActions ? props.bottomActions.showActions : undefined}
                                        actionPack={props.bottomActions ? props.bottomActions.actionPack : undefined}
                                        items={props.bottomActions ? props.bottomActions.items : undefined}
                                        actions={props.bottomActions ? props.bottomActions.actions : undefined}
                                        onPrintClick={onPrintClick}
                                        onActionCancel={onActionCancel}
                                        onActionRelease={onActionRelease}
                                        onActionClose={onActionClose}
                                        onActionPost={onActionPost}
                                        onActionApprove={onActionApprove}
                                        onActionReject={onActionReject}
                                        onAction={onAction}
                                        onActionProcessing={onActionProcessing}
                                        onActionProceeed={onActionProceeed}
                                        actionRelease={props.actionRelease}
                                        actionPost={props.actionPost}
                                    />
                                </BoxItem>
                            </Box>

                            {props.editMain && props.editMain.items instanceof Array && (
                                <AntObjectEditPopup
                                    ref={mainEditPopup}
                                    // shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                                    API={props.API}
                                    table={props.editMain && props.editMain.table ? props.editMain.table : props.table}
                                    title={props.editMain ? props.editMain.title : undefined}
                                    width={props.editMain ? props.editMain.width : undefined}
                                    height={props.editMain ? props.editMain.height : undefined}
                                    minHeight={props.editMain ? props.editMain.minHeight : undefined}
                                    maxHeight={props.editMain ? props.editMain.maxHeight : undefined}
                                    entityName={props.API.entityName ? props.API.entityName : props.entityName}
                                    entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                                    colCount={props.editMain ? props.editMain.colCount : 1}
                                    alignItemLabelsInAllGroups={props.editMain ? props.editMain.alignItemLabelsInAllGroups : undefined}
                                    labelLocation={props.editMain ? props.editMain.labelLocation : undefined}
                                    onFieldDataChanged={props.editMain ? props.editMain.onFieldDataChanged : undefined}
                                    onSaving={
                                        props.onEditing
                                            ? props.onEditing
                                            : props.editMain && props.editMain.onEditing
                                            ? props.editMain.onEditing
                                            : undefined
                                    }
                                    onSaved={(e) => {
                                        if (props.editMain && props.editMain.onSaved) props.editMain.onSaved(e);
                                        if (props.onSaved) props.onSaved(e);
                                        forceReload();
                                    }}
                                    onError={props.editMain ? props.editMain.onError : undefined}
                                    prepareConditions={
                                        props.editMain && props.editMain.prepareConditions
                                            ? props.editMain.prepareConditions
                                            : props.prepareConditions
                                    }
                                    fields={props.fields}
                                    items={props.editMain ? props.editMain.items : []}
                                    postFields={props.editMain && props.editMain.postFields instanceof Array ? props.editMain.postFields : undefined}
                                />
                            )}
                            <AntMessageBox ref={msgBox} />
                        </div>
                    )}
                </AntPage>
            )}

            {props.children}
            {/* {React.Children.map(props.children, (child) => {
                return React.cloneElement(child, {});
            })} */}
            <div style={{ display: 'none' }}>
                <AntPrinting ref={printCom} appGlobal={appGlobal} onReady={handlePrint} />
            </div>
        </AntGridScope>
    );
});
